import { identity } from 'lodash';
import { createDeepSelector } from '../../../../utils/milanoteReselect/milanoteReselect';
import { AutoCellTypeObject, CellSelections, CellTypeObject } from '../../../../../common/table/TableTypes';
import { ActiveTableSelector } from '../hotTableStoreHooks';
import { ActiveTableObject } from '../hotTableStoreTypes';
import { getHotTableCellBackgroundColor } from '../../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { getSharedFormattingValuesFromHot } from '../../utils/hotInstanceUtils/cell/hotTableCellTypeUtils';
import { getAllCellsBetween } from '../../utils/selectionUtils/tableSelectionGeometryUtils';

type SharedFormattingValues = Partial<CellTypeObject> | AutoCellTypeObject | undefined;

export const selectActiveTableCellSelection: ActiveTableSelector<CellSelections | undefined> = createDeepSelector<
    ActiveTableObject | undefined,
    unknown,
    CellSelections | undefined
>(
    // It's important that we get the selection state here, rather than in the return function, otherwise
    //  if we just pass through the activeTableState, the selector will not re-run, because the table instance reference
    //  is stable.
    (activeTableState) => activeTableState?.instance?.getSelected(),
    identity,
);

/**
 * Returns the live HOT selection only when the active V2 table matches the requested element.
 */
export const selectActiveTableCellSelectionForElement =
    (elementId: string): ActiveTableSelector<CellSelections | undefined> =>
    (activeTableState) => {
        if (!activeTableState || activeTableState.elementId !== elementId) return;
        return selectActiveTableCellSelection(activeTableState);
    };

/**
 * Get the background colour of the current cell selection.
 */
export const selectActiveTableCurrentCellBackgroundColor: ActiveTableSelector<string | null | undefined> = (
    activeTable,
) => {
    if (!activeTable?.instance) return null;
    const currentSelection = selectActiveTableCellSelection(activeTable);
    return getHotTableCellBackgroundColor(activeTable.instance, currentSelection);
};

/**
 * Returns the shared cell type formatting values for the active selection using live HoT cell metadata.
 */
export const selectActiveTableSharedFormattingValues: ActiveTableSelector<SharedFormattingValues> = createDeepSelector<
    ActiveTableObject | undefined,
    CellSelections | undefined,
    ActiveTableObject | undefined,
    SharedFormattingValues | undefined
>(selectActiveTableCellSelection, identity, (currentSelection, activeTableState) => {
    if (!activeTableState || !currentSelection?.length) return;

    return getSharedFormattingValuesFromHot(activeTableState.instance, getAllCellsBetween(currentSelection));
});
